import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaShoppingCart, FaTimes, FaArrowRight } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import displayINRCurrency from '../helpers/displayCurrency';

const CartReminderPopup = ({ onClose }) => {
  const navigate = useNavigate();
  const { cartItems } = useSelector((state) => state.cart);
  const [showItems, setShowItems] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {
    // Filter out placeholder items and show up to 3 items
    const validItems = cartItems.filter(item => item.id !== 0 && item.variantDetails);
    setShowItems(validItems.slice(0, 3));
    
    // Calculate total amount
    const total = validItems.reduce((sum, item) => {
      return sum + (item.quantity * (item.variantDetails?.sellingPrice || 0));
    }, 0);
    setTotalAmount(total);
  }, [cartItems]);

  if (cartItems.length === 0 || (cartItems.length === 1 && cartItems[0].id === 0)) return null;

  const handleViewCart = () => {
    navigate('/cart');
    onClose();
  };

  const handleProductClick = (item) => {
    if (item.variantDetails?.publicId && item.productDetails?.publicId) {
      navigate(`/product/${item.productDetails.publicId}-${item.variantDetails.publicId}`);
      onClose();
    }
  };

  const validCartItems = cartItems.filter(item => item.id !== 0 && item.variantDetails);
  const totalItems = validCartItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all animate-scaleIn">
        {/* Header with orange gradient */}
        <div className="bg-gradient-to-r from-orange-500 via-orange-600 to-yellow-500 rounded-t-2xl p-6 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors p-1 rounded-full hover:bg-white hover:bg-opacity-20"
            aria-label="Close"
          >
            <FaTimes className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center backdrop-blur-sm">
              <FaShoppingCart className="text-white text-xl animate-bounce" />
            </div>
            <div>
              <h3 className="text-white text-xl font-bold">Complete Your Order!</h3>
              <p className="text-white text-opacity-90 text-sm">
                {totalItems} item{totalItems > 1 ? 's' : ''} waiting in your cart
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          <p className="text-gray-700 mb-4 text-sm">
            Your items are ready to checkout. Complete your purchase now!
          </p>

          {/* Cart Items Preview */}
          <div className="space-y-3 mb-4 max-h-48 overflow-y-auto">
            {showItems.map((item, index) => (
              <div
                key={`${item.id || item.cartPublicId}-${index}`}
                onClick={() => handleProductClick(item)}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 cursor-pointer transition-colors border border-gray-200"
              >
                <div className="w-16 h-16 bg-white rounded-lg overflow-hidden flex-shrink-0 border border-gray-200">
                  <img
                    src={item.variantDetails?.images?.[0] || '/placeholder-product.png'}
                    alt={item.variantDetails?.label || 'Product'}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = '/placeholder-product.png';
                    }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-gray-900 text-sm line-clamp-2">
                    {item.variantDetails?.label || 'Product'}
                  </h4>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-orange-600 font-bold text-sm">
                      {displayINRCurrency(item.variantDetails?.sellingPrice || 0)}
                    </p>
                    <span className="text-gray-400 text-xs">×</span>
                    <p className="text-gray-600 text-sm font-medium">{item.quantity}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Total Amount */}
          <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl p-4 mb-4 border border-orange-200">
            <div className="flex justify-between items-center">
              <span className="text-gray-700 font-semibold">Total Amount:</span>
              <span className="text-orange-600 font-bold text-xl">
                {displayINRCurrency(totalAmount)}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleViewCart}
              className="flex-1 bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2 shadow-lg"
            >
              <FaShoppingCart className="w-4 h-4" />
              Go to Cart
              <FaArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors duration-200"
            >
              Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartReminderPopup;

